import React from "react";
import { useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import { useFormik } from "formik";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Dialog from "src/components/Dialog";
import Box from "@material-ui/core/Box";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import ZonedKeyboardDatePicker from "src/components/ZonedKeyboardDatePicker";

import NumberField from "src/components/NumberField";
import LoadableButton from "src/components/LoadableButton";
import { addReconciliation } from "src/apiService/modules/reconciliations";
import { useRecordEvent } from "src/hooks/useRecordEvent";
import { effectiveUserTimeZoneSelector } from "src/store/system/selector";
import type { Reconciliation } from "src/interfaces/reconciliation.interface";
import {
  calendarYmdInTimeZone,
  endOfCalendarDayContainingInstant,
  tryYmdStringToEndOfDayUtc,
} from "src/utils/timezone";
import type { BankAccountWithReconciliations } from "./types";

const useStyles = makeStyles({
  content: {
    "&:first-child": {
      paddingTop: 0,
    },
  },
});

interface ContentProps {
  onClose: (dialog?: "manual-reconciliation") => void;
  bankAccount: BankAccountWithReconciliations;
  onReconciliationUpdated: (reconciliation: Reconciliation) => void;
}

function Content({
  bankAccount,
  onClose,
  onReconciliationUpdated,
}: ContentProps) {
  const recordEvent = useRecordEvent();

  const timeZone = useSelector(effectiveUserTimeZoneSelector);
  const lastReconciliation =
    bankAccount.reconciliations[bankAccount.reconciliations.length - 1];
  const lastReconciliationDate = lastReconciliation?.date
    ? tryYmdStringToEndOfDayUtc(lastReconciliation.date, timeZone)
    : undefined;
  const account = bankAccount.name;
  const classes = useStyles();
  const formik = useFormik({
    initialValues: {
      date: null,
      balance: null,
    } as { date: Date | null; balance: number | null },
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (!values.balance && values.balance !== 0)
        errors.balance = "Required field";
      if (!values.date) errors.date = "Required field";
      return errors;
    },
    onSubmit: async (values) => {
      if ((!values.balance && values.balance !== 0) || !values.date) return;

      if (bankAccount.reconciliations.length) {
        const dateYmd = calendarYmdInTimeZone(values.date, timeZone);
        const created = (await addReconciliation(
          bankAccount.id,
          values.balance,
          dateYmd
        )) as { open?: boolean; reconciliation?: Reconciliation };
        if (created.reconciliation) {
          onReconciliationUpdated(created.reconciliation);
        }
        if (!created?.open) {
          recordEvent({
            type: "feature-adoption",
            feature: "bank-reconciliation-success",
          });
          onClose();
        } else {
          onClose("manual-reconciliation");
        }
      } else {
        const dateYmd = calendarYmdInTimeZone(values.date, timeZone);
        const created = (await addReconciliation(
          bankAccount.id,
          values.balance,
          dateYmd
        )) as { reconciliation?: Reconciliation };
        if (created.reconciliation) {
          onReconciliationUpdated(created.reconciliation);
        }
        recordEvent({
          type: "feature-adoption",
          feature: "bank-reconciliation-success",
        });
        onClose();
      }
    },
    validateOnBlur: false,
    validateOnChange: false,
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <DialogContent className={classes.content}>
        <TextField
          value={account}
          inputProps={{ readOnly: true }}
          margin="dense"
          variant="outlined"
          fullWidth
          label="Account"
        />
        <NumberField
          allowNegative
          autoComplete="off"
          margin="dense"
          fullWidth
          required
          label="Ending Balance"
          value={formik.values.balance ?? ""}
          variant="outlined"
          onChange={formik.handleChange}
          name="balance"
          helperText={formik.errors?.balance || undefined}
          error={!!formik.errors?.balance}
          disabled={formik.isSubmitting}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
        />
        <Box pt={1}>
          <ZonedKeyboardDatePicker
            fullWidth
            variant="inline"
            inputVariant="outlined"
            disableToolbar
            format="MM/dd/yyyy"
            label="Ending Date"
            required
            autoOk
            onChange={(d) => {
              if (d) {
                const date = endOfCalendarDayContainingInstant(d, timeZone);
                formik.setValues((v) => ({
                  ...v,
                  date,
                }));
              } else {
                formik.setValues((v) => ({
                  ...v,
                  date: null,
                }));
              }
            }}
            value={formik.values.date || null}
            helperText={formik.errors?.date || undefined}
            error={!!formik.errors?.date}
            disabled={formik.isSubmitting}
            minDate={lastReconciliationDate}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <LoadableButton
          color="primary"
          variant="contained"
          type="submit"
          loading={formik.isSubmitting}
        >
          Add
        </LoadableButton>
      </DialogActions>
    </form>
  );
}

function AddReconciliationDialog({
  open,
  onClose,
  bankAccount,
  onReconciliationUpdated,
}: ContentProps & { open: boolean }) {
  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>New Reconciliation</DialogTitle>
      <Content
        bankAccount={bankAccount}
        onClose={onClose}
        onReconciliationUpdated={onReconciliationUpdated}
      />
    </Dialog>
  );
}

export default AddReconciliationDialog;
